<template>
  <button
    :class="['base-icon-button', type, { 'round': !hasSlotContent, 'disabled': disabled }]"
    :disabled="disabled"
    @click="handleClick"
  >
    <font-awesome-icon
      v-if="loading"
      :icon="['fal', 'spinner']"
      spin
    />
    <font-awesome-icon
      v-else-if="icon && icon.trim()"
      :icon="[iconPrefix, cleanIconName]"
    />
    <slot />
  </button>
</template>

<script setup>
import { useSlots, computed } from 'vue';

const props = defineProps({
  icon: {
    type: String,
    required: true,
    validator: (value) => value && value.trim().length > 0,
  },
  iconPrefix: {
    type: String,
    default: 'fal',
  },
  type: {
    type: String,
    default: 'default',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const cleanIconName = computed(() => {
  return props.icon.replace(/^fa-/, '');
});

const emit = defineEmits(['click']);

function handleClick (event) {
  emit('click', event);
}

const slots = useSlots();
const hasSlotContent = computed(() => !!slots.default);
</script>

<style scoped>
.base-icon-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  transition: background-color 0.2s, color 0.2s;
}

.base-icon-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.base-icon-button.default {
  color: var(--color-secondary);
}

.base-icon-button.primary {
  color: var(--color-primary);
}

.base-icon-button.delete {
  color: var(--color-danger);
}

.base-icon-button.cancel {
  color: var(--color-secondary);
}

.base-icon-button.info {
  color: var(--color-info);
}

.base-icon-button.warning {
  color: var(--color-warning);
}

.base-icon-button.danger {
  color: var(--color-danger);
}

.base-icon-button.environment {
  color: var(--logo-color-primary);
  background-color: var(--background-color);
}

/* Hover states */
.base-icon-button.default:hover,
.base-icon-button.cancel:hover,
.base-icon-button.round.default:hover,
.base-icon-button.round.cancel:hover {
  background-color: var(--color-secondary);
  color: white;
}

.base-icon-button.primary:hover,
.base-icon-button.round.primary:hover {
  background-color: var(--color-primary);
  color: white;
}

.base-icon-button.delete:hover,
.base-icon-button.round.delete:hover {
  background-color: var(--color-danger);
  color: white;
}

.base-icon-button.info:hover,
.base-icon-button.round.info:hover {
  background-color: var(--color-info);
  color: white;
}

.base-icon-button.warning:hover,
.base-icon-button.round.warning:hover {
  background-color: var(--color-warning);
  color: black;
}

.base-icon-button.danger:hover,
.base-icon-button.round.danger:hover {
  background-color: var(--color-danger);
  color: white;
}

.base-icon-button.round {
  border-radius: 50%;
  padding: 8px;
  width: 36px;
  height: 36px;
  justify-content: center;
}

.base-icon-button.round:hover {
  background-color: inherit;
}

.base-icon-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.base-icon-button.disabled:hover {
  background: none;
  color: inherit;
}

.base-icon-button.environment:hover {
  background-color: var(--logo-color-primary);
  color: var(--background-color);
}


/* Add specific disabled hover states for each type */
.base-icon-button.default.disabled:hover {
  color: var(--color-secondary);
}

.base-icon-button.primary.disabled:hover {
  color: var(--color-primary);
}

.base-icon-button.danger.disabled:hover {
  color: var(--color-danger);
}
</style>
